import { Timestamp } from 'firebase/firestore';

type TimestampLike = {
  _nanoseconds?: unknown;
  _seconds?: unknown;
  nanoseconds?: unknown;
  seconds?: unknown;
  toDate?: unknown;
  toMillis?: unknown;
};

function finiteNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function validDate(date: Date) {
  return Number.isFinite(date.getTime()) ? date : null;
}

export function toDate(value: unknown) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return validDate(value);
  }

  if (value instanceof Timestamp) {
    return validDate(value.toDate());
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return validDate(new Date(value));
  }

  if (typeof value !== 'object') {
    return null;
  }

  const timestamp = value as TimestampLike;

  if (typeof timestamp.toDate === 'function') {
    try {
      return validDate(timestamp.toDate());
    } catch {
      return null;
    }
  }

  if (typeof timestamp.toMillis === 'function') {
    try {
      return validDate(new Date(timestamp.toMillis()));
    } catch {
      return null;
    }
  }

  const seconds = finiteNumber(timestamp.seconds ?? timestamp._seconds);
  const nanoseconds = finiteNumber(
    timestamp.nanoseconds ?? timestamp._nanoseconds,
  );

  if (seconds === null) {
    return null;
  }

  return validDate(new Date(seconds * 1000 + Math.floor((nanoseconds ?? 0) / 1e6)));
}

export function formatDate(value: unknown) {
  const date = toDate(value);

  if (!date) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export function formatDateOnly(value: unknown) {
  const date = toDate(value);

  if (!date) {
    return 'Not available';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
  }).format(date);
}

export function formatRelativeTime(value: unknown) {
  const date = toDate(value);

  if (!date) {
    return '';
  }

  const elapsedMs = Date.now() - date.getTime();

  if (!Number.isFinite(elapsedMs)) {
    return '';
  }

  if (elapsedMs < 60_000) {
    return 'now';
  }

  const elapsedMinutes = Math.floor(elapsedMs / 60_000);

  if (elapsedMinutes < 60) {
    return `${elapsedMinutes}m`;
  }

  const elapsedHours = Math.floor(elapsedMinutes / 60);

  if (elapsedHours < 24) {
    return `${elapsedHours}h`;
  }

  const elapsedDays = Math.floor(elapsedHours / 24);

  if (elapsedDays < 7) {
    return `${elapsedDays}d`;
  }

  const elapsedWeeks = Math.floor(elapsedDays / 7);

  if (elapsedWeeks < 5) {
    return `${elapsedWeeks}w`;
  }

  const elapsedMonths = Math.floor(elapsedDays / 30);

  if (elapsedMonths < 12) {
    return `${Math.max(1, elapsedMonths)}mo`;
  }

  return `${Math.floor(elapsedDays / 365)}y`;
}
